import { createRouter, createWebHistory, type RouteLocationRaw } from 'vue-router';
import { authService } from '@/services/auth.service';
import Community from '@/views/PageCommunity.vue';
import Page from '@/views/Page.vue';
import About from '@/views/PageAbout.vue';
import NotFound from '@/views/PageNotFound.vue';
import Profile from '@/views/PageProfile.vue';
import { paths } from '@/utils/paths';
import PropositionsPage from '@/components/PropositionsPage.vue';
import ConsultationsPage from '@/views/PageConsultation.vue';
import { useCommunityStore } from '@/store/communityStore';
import { useConfigurationStore } from '@/store/configurationStore';
import { communitiesService } from '@/services/communities.service';
import { customPageService } from '@/services/customPage.service';
import ProjectPage from '@/views/PageProject.vue';
import { useUserStore } from '@/store/userStore';
import { projectsService } from '@/services/projects.service';
import ProjectExpansionPage from '@/views/PageProjectExpansion.vue';
import { expansionPageKeys } from '@/themes/types';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        top: 0,
      };
    }

    return { top: 0 };
  },
  routes: [
    {
      path: paths.root,
      name: 'home',
      component: Community,
    },
    {
      path: paths.consultations_list,
      name: 'consultations-list',
      component: Community,
    },
    {
      path: paths.login,
      name: 'login',
      component: { template: '<div></div>' },
      beforeEnter: async (to, from, next) => {
        let nextRoute: RouteLocationRaw = '/';
        if (to.query.consultation) {
          let query = `consultations/${to.query.consultation as string}`;
          if (to.query.section) {
            query += `?section=${to.query.section as string}`;
            if (to.query.form) {
              query += `&form=${to.query.form as string}`;
            }
          }
          nextRoute = `/${query}`;
        } else if (to.query.projectSlug) {
          const communityStore = useCommunityStore();
          const dtoProject = await projectsService.getProjectBySlug(to.query.projectSlug as string);
          const project = communityStore.getProject(dtoProject.id);
          nextRoute = {
            path: `/projects/${project.slug}`,
            hash: to.query.projectHash ? `#${to.query.projectHash as string}` : undefined,
          };
        } else if (to.query.pageSlug || to.query.pageHomepage) {
          // Back to the draft preview that asked for the sign-in, as a named route: the return names a page, never a
          // path, so it cannot send the administrator anywhere else.
          const hash = to.query.pageHash ? `#${to.query.pageHash as string}` : undefined;
          nextRoute = to.query.pageSlug
            ? { name: 'page', params: { slug: to.query.pageSlug as string }, query: { preview: 'draft' }, hash }
            : { name: 'home', query: { preview: 'draft' }, hash };
        }
        next(nextRoute);
      },
    },
    {
      path: paths.logout,
      name: 'logout',
      component: { template: '<div></div>' },
      beforeEnter: async (to, from, next) => {
        await authService.logoutUser();
        next('/');
      },
    },
    {
      path: paths.consultations,
      name: 'consultations',
      component: ConsultationsPage,
      props: {
        isTemplate: false,
      },
    },
    {
      path: paths.consultationTemplates,
      name: 'consultationTemplates',
      component: ConsultationsPage,
      props: {
        isTemplate: true,
      },
    },
    {
      path: paths.project,
      name: 'project',
      component: ProjectPage,
      props: {
        isTemplate: false,
      },
    },
    {
      path: paths.projectTemplate,
      name: 'projectTemplate',
      component: ProjectPage,
      props: {
        isTemplate: true,
      },
    },
    {
      path: paths.projectNewsItem,
      name: 'projectNewsItem',
      component: ProjectExpansionPage,
      props: {
        isTemplate: false,
        expansionKey: expansionPageKeys.NewsItem,
      },
    },
    {
      path: paths.propositions,
      name: 'propositions',
      component: PropositionsPage,
    },
    {
      path: paths.about,
      name: 'about-us',
      component: About,
    },
    {
      path: paths.profile,
      name: 'profile',
      component: Profile,
    },
    {
      path: '/:locale(en|fr)',
      name: 'locale',
      component: Community,
    },
    {
      path: '/not-found',
      name: 'not-found',
      component: NotFound,
    },
    {
      path: paths.page,
      name: 'page',
      component: Page,
      props: (route) => ({
        slug: route.params.slug as string,
      }),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (to.name === 'login' && to.query.token) {
    const token = to.query.token as string;
    await authService.loginUser(token);
  } else if (!useUserStore().user?.id) {
    await authService.initUser();
  }
  const communityStore = useCommunityStore();
  const configurationStore = useConfigurationStore();

  await Promise.all([
    !configurationStore.isLoaded ? configurationStore.init() : Promise.resolve(),
    !communityStore.isLoaded
      ? (async () => {
          const community = await communitiesService.getCommunity();
          if (community) {
            communityStore.setCommunity(community);
            await customPageService.getCustomPages();
          }
        })()
      : Promise.resolve(),
  ]);

  next();
});
export default router;
